import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { action, computed, reaction } from 'mobx';
import * as classNames from 'classnames';
import { includes } from 'lodash';
import { IAppOnlyStore } from '../../../stores/AllStores';
import { CSSTransitionGroup } from 'react-transition-group';
import AnimateHeight from 'react-animate-height';
import { Button, EButtonType } from '../../action/index';

// Enumerables
export enum CategoriesProperties {
    itemGroup = 'itemGroup',
    certCategory = 'certCategory'
}

// Styles
const s = require('./Categories.css');
const checkmarkIcon = require('./../../../assets/checkmark-icon.svg');
const collapseIcon = require('./../../../assets/collapse-arrows.svg');
const trashCanIcon = require('./../../../assets/trash-can-icon.svg');
const tickBlueIcon = require('./../../../assets/tick-blue.svg');

// Component
@inject('store')
@observer
export class Categories extends React.Component<IAppOnlyStore, {}> {
    private reactToShowCategoriesChange = reaction(
        () => this.props.store.categories.showCategories,
        (show: boolean) => {
            if (show) {
                setTimeout(() => {
                    if (this.searchFiltersField && typeof this.searchFiltersField.focus !== 'undefined') {
                        this.searchFiltersField.focus();
                    }
                }, 256);
            }
        }
    );

    @computed private get hasSelection(): boolean {
        const { search: { searchInputValues: { itemGroup, certCategory } } } = this.props.store;
        return itemGroup.length > 0 || certCategory.length > 0;
    }

    @computed private get noResults(): boolean {
        const { categories } = this.props.store;
        return categories.certificationSearchedResults.length <= 0 && categories.categoriesSearchedResults.length <= 0;
    }

    @computed private get showNoResultsMessage(): boolean {
        const { search: { searchInputValues: { itemGroup, certCategory } } } = this.props.store;
        return this.noResults && itemGroup.length <= 0 && certCategory.length <= 0;
    }

    public render() {
        const { search: { searchInputValues: { itemGroup, certCategory } }, categories } = this.props.store;

        return (
            <div
                onClick={this.handleBackgroundClick}
                className={classNames(s.categories, {
                    [s.show]: categories.showCategories
                })}
            >
                <AnimateHeight className={s.animateHeight} duration={500} height={categories.showCategories ? 'auto' : 0}>
                    <div className={s.wrapper} onClick={this.handleCategoriesClick}>

                        <div className={s.header}>
                            <div className={s.content}>
                                <div>
                                    {(this.hasSelection) && (
                                        <Button
                                            tabIndex={1}
                                            inBox={true}
                                            className={s.clear}
                                            type={EButtonType.Important}
                                            icon={{
                                                className: classNames(s.buttonsIcons, {
                                                    [s.whiteBackground]: true
                                                }),
                                                element: <img
                                                    src={trashCanIcon}
                                                    width={11}
                                                    height={13}
                                                    alt=""
                                                />
                                            }}
                                            onClick={this.handleClearFilters}
                                        >
                                            Clear all filters
                                        </Button>
                                    )}
                                </div>
                                <div className={s.middle}>
                                    <input
                                        ref={this.referenceSearchFiltersField}
                                        type="text"
                                        tabIndex={3}
                                        placeholder="Search filters..."
                                        value={categories.filterQuery}
                                        onChange={this.handleSearchFilter}
                                    />
                                </div>
                                <div>
                                    <Button
                                        tabIndex={2}
                                        inBox={true}
                                        onClick={this.handleDismiss}
                                        type={this.hasSelection ? EButtonType.Action : EButtonType.Default}
                                        icon={{
                                            className: classNames(s.buttonsIcons, {
                                                [s.whiteBackground]: this.hasSelection
                                            }),
                                            element: <img
                                                src={this.hasSelection ? tickBlueIcon : collapseIcon}
                                                width={this.hasSelection ? 12 : 11}
                                                height={this.hasSelection ? 10 : 11}
                                                alt=""
                                            />
                                        }}
                                        className={classNames(s.hide, {
                                            [s.apply]: this.hasSelection
                                        })}
                                    >
                                        {this.hasSelection
                                            ? 'Apply selected filters'
                                            : 'Hide filters'
                                        }
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className={s.lists}>
                            <div className={s.listsContainer}>

                                <ul className={classNames(s.certificationList)}>
                                    <li key="filter-by-certification" className={s.label}>
                                        <p>Filter by certification:</p>
                                    </li>

                                    {categories.certificationSearchedResults.map((item) => {
                                        return (
                                            <li
                                                key={item.name}
                                                className={classNames(s.item, {
                                                    [s.checked]: item.checked,
                                                    [s.visible]: item.visible,
                                                    [s.crossed]: item.crossed
                                                })}
                                                onClick={
                                                    this.handleCategoryChange(item.name, CategoriesProperties.certCategory)
                                                }
                                            >
                                                <i>
                                                    {(item.checked) && (
                                                        <img src={checkmarkIcon} />
                                                    )}
                                                </i>
                                                <button>{item.name}</button>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <ul className={classNames(s.itemGroupsList)}>
                                    <li key="filter-by-item-group" className={s.label}>
                                        <p>Filter by item group:</p>
                                    </li>

                                    {categories.categoriesSearchedResults.map((item) => {
                                        return (
                                            <li
                                                key={item.name}
                                                className={classNames(s.item, {
                                                    [s.checked]: item.checked,
                                                    [s.visible]: item.visible,
                                                    [s.crossed]: item.crossed
                                                })}
                                                onClick={
                                                    this.handleCategoryChange(item.name, CategoriesProperties.itemGroup)
                                                }
                                            >
                                                <i>
                                                    {(item.checked) && (
                                                        <img src={checkmarkIcon} />
                                                    )}
                                                </i>
                                                <button>{item.name}</button>
                                            </li>
                                        );
                                    })}
                                </ul>

                            </div>
                        </div>

                    </div>
                </AnimateHeight>
            </div>
        );
    }

    private searchFiltersField: HTMLInputElement = null;
    private referenceSearchFiltersField = (ref) => this.searchFiltersField = ref;

    private handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
        this.handleDismiss();

        e.preventDefault();
        e.stopPropagation();
    }

    private handleCategoriesClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    }

    @action private handleClearFilters = () => {
        let { categories, search } = this.props.store;
        this.handleDismiss();
        categories.filterQuery = '';
        search.searchInputValues.modifiedGroups = true;
        search.searchInputValues.certCategory = [];
        search.searchInputValues.itemGroup = [];
    };

    @action private handleSearchFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.store.categories.filterQuery = e.currentTarget.value;
    }

    @action private handleDismiss = () => {
        this.props.store.categories.showCategories = false;
        this.props.store.search.searchInputValues.modifiedGroups = true;

        setTimeout(() => {
            this.props.store.categories.filterQuery = '';
        }, 800);
    };

    @action private handleCategoryChange =
        (item: string, property: CategoriesProperties) =>
        (e: React.MouseEvent<HTMLLIElement>) => {
        const { searchInputValues } = this.props.store.search;

        if (includes(searchInputValues[property], item)) {
            searchInputValues[property].splice(searchInputValues[property].indexOf(item), 1);
        } else {
            searchInputValues[property].push(item);
        }
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/layout/PortalSearchBar/Categories.tsx